import { Box, Dialog, DialogActions, DialogContent, Divider, Typography } from "@mui/material"
import dayjs from "dayjs"
import type { FC } from "react"

import { Button } from "@/components/common/Button"
import DisplayAS400Id from "@/components/common/DisplayAS400Id/DisplayAS400Id"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { FilesTable } from "@/components/Tables/Aeros/components/FilesTable"
import { useFileColumns } from "@/components/Tables/Aeros/hooks/useFileColumns"
import { useGetFileUploadData } from "@/components/upload/hooks/useGetFileUploadData"
import { DATE_FORMAT } from "@/constants/formats"
import type { AdjustmentHistoryItem } from "@/graphql/codegen/graphql"
import { ReferenceTableName } from "@/graphql/codegen/graphql"

interface AdjustmentHistoryDocumentsDialogProps {
  item: AdjustmentHistoryItem
  onClose: () => void
}

export const AdjustmentHistoryDocumentsDialog: FC<AdjustmentHistoryDocumentsDialogProps> = ({ item, onClose }) => {
  const { fileList, fileFetching } = useGetFileUploadData({
    referenceId: item.inventoryAdjustmentId,
    referenceTableName: ReferenceTableName.InventoryAdjustment,
  })

  const columns = useFileColumns({ typeToChip: {}, canDelete: false })

  return (
    <Dialog open onClose={onClose} maxWidth='md' fullWidth>
      <DialogHeader title='Documents' onClose={onClose} />
      <DialogContent className='p-10'>
        <Box className='flex flex-col gap-6'>
          <Box className='flex flex-col gap-3'>
            <Typography className='text-base font-bold text-gray-700'>
              {item.product.companyProductDescription}
            </Typography>
            <Box className='flex items-start gap-10'>
              <Box className='flex flex-col gap-1'>
                <Typography className='text-sm font-bold text-gray-700'>Product ID</Typography>
                <Typography variant='body2' className='text-gray-700'>
                  {item.product.publicId ?? item.product.productId}
                </Typography>
                {item.product.externalSystemId && <DisplayAS400Id id={item.product.externalSystemId} />}
              </Box>
              <Box className='flex flex-col gap-1'>
                <Typography className='text-sm font-bold text-gray-700'>Date</Typography>
                <Typography variant='body2' className='text-gray-700'>
                  {dayjs(item.date).format(DATE_FORMAT.US)}
                </Typography>
              </Box>
              <Box className='flex flex-col gap-1'>
                <Typography className='text-sm font-bold text-gray-700'>Adjustment #</Typography>
                <Typography variant='body2' className='text-gray-700'>
                  {item.adjustmentReferenceNbr}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box className='flex flex-col gap-2.5'>
            <Typography className='text-base font-medium text-gray-700'>View Documents</Typography>
            <Divider />
          </Box>

          <FilesTable data={fileList} isLoading={fileFetching} columns={columns} />
        </Box>
      </DialogContent>

      <DialogActions className='flex items-center justify-end px-10 pb-10'>
        <Button appearance='contained' onClick={onClose} data-testid='adjustment-history.documents-dialog.close-button'>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
